import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, Users, FileText, Settings, LogOut, Sparkles, BarChart3,
} from "lucide-react";

const BRAND_LOGO = "https://customer-assets.emergentagent.com/job_07bf3866-9731-403a-a6ee-e65c36e8e48f/artifacts/x210xibv_BillMerch%20-%20Full%20Colour%20-%20Assinarura.png";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/cotacoes", label: "Cotações", icon: FileText, testid: "nav-quotes" },
  { to: "/clientes", label: "Clientes", icon: Users, testid: "nav-clients" },
  { to: "/analitico", label: "Análise", icon: BarChart3, testid: "nav-analytics" },
  { to: "/configuracoes", label: "Configurações", icon: Settings, testid: "nav-settings" },
];

const Sidebar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-zinc-800/80 bg-[#0b0b0d] h-screen sticky top-0">
      <div className="px-6 pt-7 pb-6 border-b border-zinc-800/60">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-black border border-zinc-700 flex items-center justify-center overflow-hidden">
            <img src={BRAND_LOGO} alt="BillMerch" className="w-full h-full object-contain" />
          </div>
          <div>
            <div className="font-display text-base font-bold tracking-tight text-silver-gradient">BillMerch</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">CRM Studio</div>
          </div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-6 space-y-1">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            data-testid={item.testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all
              ${isActive
                ? "bg-zinc-100 text-zinc-900 shadow-[0_0_18px_rgba(255,255,255,0.06)]"
                : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/60"}`
            }
          >
            <item.icon className="w-[18px] h-[18px]" strokeWidth={1.5} />
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="px-3 pb-4 mt-auto">
        <div className="rounded-xl border border-zinc-800 bg-[#121214] p-4 mb-3">
          <div className="flex items-center gap-2 mb-1.5">
            <Sparkles className="w-3.5 h-3.5 text-zinc-400" strokeWidth={1.5} />
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">Sessão</div>
          </div>
          <div className="text-sm text-zinc-200 font-medium truncate" data-testid="sidebar-user-email">{user?.email}</div>
          <div className="text-xs text-zinc-500">{user?.role}</div>
        </div>
        <button
          onClick={async () => { await logout(); navigate("/login"); }}
          data-testid="logout-btn"
          className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/80 border border-zinc-800/80 transition-all"
        >
          <LogOut className="w-4 h-4" strokeWidth={1.5} /> Sair
        </button>
      </div>
    </aside>
  );
};

const TopBar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="md:hidden sticky top-0 z-30 bg-[#0b0b0d]/95 backdrop-blur border-b border-zinc-800/80">
      <div className="flex items-center justify-between px-4 h-14">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-black border border-zinc-700 flex items-center justify-center overflow-hidden">
            <img src={BRAND_LOGO} alt="BillMerch" className="w-full h-full object-contain" />
          </div>
          <span className="font-display font-bold text-silver-gradient">BillMerch</span>
        </div>
        <button onClick={async () => { await logout(); navigate("/login"); }} className="text-zinc-400 p-2">
          <LogOut className="w-5 h-5" />
        </button>
      </div>
      <div className="px-2 pb-2 flex gap-1 overflow-x-auto">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              `flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap
              ${isActive ? "bg-zinc-100 text-zinc-900" : "text-zinc-400 bg-zinc-900/60"}`
            }
          >
            <item.icon className="w-3.5 h-3.5" strokeWidth={1.5} />
            {item.label}
          </NavLink>
        ))}
      </div>
    </div>
  );
};

const Layout = ({ children }) => {
  return (
    <div className="flex min-h-screen bg-[#09090b]">
      <Sidebar />
      <div className="flex-1 min-w-0 flex flex-col">
        <TopBar />
        <main className="flex-1 p-6 md:p-10">{children}</main>
      </div>
    </div>
  );
};

export default Layout;
